import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useJobsStore, useConfigStore } from './stores';
import { useJobDrafts } from './drafts';
import { jobFormModal, closeJobForm } from './job-form-modal';
import {
  WORKING_TIME_PRESETS,
  getEmptyJobForm,
  loadJobFormFromRecord,
  inferPaymentDueMode,
  computeAutoPaymentDueInput,
  formatDateForInput,
  getWorkingTimeInputPart,
  getWorkingTimePart,
  setWorkingTimePart,
  normalizeWorkingTimeInput,
  validateWorkingTime,
  calculateQuotedTotal,
  parseDepositSchedule,
  serializeDepositSchedule,
  sumDepositSchedule,
  parseEventDayTimes,
  serializeEventDayTimes,
  type DepositInstallment,
  useClientTemplate,
  buildEventDateValue,
  formatDateToSheet,
  type ClientTemplate,
  type JobFormRecord,
} from './job-form-helpers';
import {
  buildQuotationLayoutPreviewHtml,
  parseQuotationLayoutFromConfig,
  GOOGLE_CALENDAR_EVENT_COLORS,
  getGoogleCalendarEventColor,
  normalizeGoogleCalendarColorId,
  formatEventDateDisplayCompact,
  JOB_TYPE_PERSONAL,
  PERSONAL_JOB_TAG,
  getDefaultQuotationLayoutBlocks,
  getInstallmentLabel,
  parsePaymentLedger,
  summarizePayments,
  validatePaymentData,
  getJobDocumentHistory,
  hasDocumentVersionForScope,
  DOCUMENT_LABELS,
  documentFileKey,
  formatDateKey,
  money,
  type PaymentLedger,
  type PaymentEntry,
  type DocumentType,
} from '@qjob/shared';
import { formatPaymentDate, formatCurrency, loadJobDrafts, normalizeJobStatus, getJobFolderUrl } from './utils';
import { ApiRequestError, api, getSignatureImageUrl, getDocumentDownloadUrl } from './api';
import { useDocumentAvailability } from './useDocumentAvailability';
import { confirmModal, showConfirmModal, showAlertModal } from './confirm-modal';
import {
  SLIP_MAX_FILES,
  SLIP_MAX_FILE_SIZE_LABEL,
  getSlipPreviewUrl,
  isAcceptedSlipFile,
  isSlipPdf,
  parseSlipReferences,
  prepareSlipFileForUpload,
  serializeSlipReferences,
  slipDisplayName,
  SLIP_FILE_ACCEPT,
  SLIP_SUPPORTED_FORMATS_LABEL,
  type SlipReference,
} from './slip-upload-helpers';

declare const flatpickr: any;

interface CalendarReminder {
  method: string;
  value: number;
  unit: string;
}

interface JobFormReminderSettings {
  useDefault: boolean;
  reminders: CalendarReminder[];
}

interface LocationMapFavorite {
  id: string;
  name: string;
  location: string;
  mapUrl: string;
  favorite: boolean;
}

function getDefaultCalendarReminderSettings(): JobFormReminderSettings {
  return {
    useDefault: true,
    reminders: [
      { method: 'popup', value: 1, unit: 'days' },
      { method: 'popup', value: 4, unit: 'hours' },
    ],
  };
}

function parseClientTemplates(raw: string): ClientTemplate[] {
  try {
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseLocationMapFavorites(raw: unknown): LocationMapFavorite[] {
  try {
    const parsed = JSON.parse(String(raw || '[]'));
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item === 'object')
      .map((item: any, index: number) => ({
        id: String(item.id || `location-${index + 1}`),
        name: String(item.name || '').trim(),
        location: String(item.location || '').trim(),
        mapUrl: String(item.mapUrl || '').trim(),
        favorite: item.favorite === true || String(item.favorite).toLowerCase() === 'true',
      }))
      .filter((item: LocationMapFavorite) => !!item.name && (!!item.location || !!item.mapUrl));
  } catch {
    return [];
  }
}

export function useJobFormModal() {
  const router = useRouter();
  const { state, reload, save, remove } = useJobsStore();
  const { state: configStore, reload: reloadConfig, save: saveConfig } = useConfigStore();
  const { add: addDraft, update: updateDraft, remove: removeDraft } = useJobDrafts();

  const form = ref<JobFormRecord>(getEmptyJobForm());
  const depositInstallments = ref<DepositInstallment[]>([]);
  const paymentLedger = ref<PaymentLedger>({ version: 1, openingReceived: 0, entries: [] });
  const paymentEntryDraft = reactive({ open: false, installmentId: '', amount: '' as string | number, paidDate: '', method: 'โอนเงิน', reference: '', note: '', slipUrl: '' });
  const paymentEntryError = ref('');
  const isUploadingPaymentSlip = ref(false);
  const paymentSlipFileName = ref('');
  const showDocumentHistory = ref(false);
  const receiptPaymentId = ref('');
  const quotationPaymentSelectionId = ref('');
  const legacyPaymentReviewRequired = ref(false);
  const legacyReceivedDraft = ref('');
  const sameTimeForAllDays = ref(true);
  const eventDayTimes = reactive<Record<string, string>>({});
  const paymentDueMode = ref<'auto' | 'manual' | 'none'>('auto');
  const saving = ref(false);
  const generatingDocuments = ref(false);
  const deleting = ref(false);
  const syncingCalendar = ref(false);
  const sendingLine = ref(false);
  const error = ref('');
  const success = ref('');
  const generatedDocuments = ref<{ label: string; number?: string; pdfUrl: string }[]>([]);
  const generatedFolderUrl = ref('');
  const selectedCompanyTemplateIndex = ref('');
  const isUploadingSlip = ref(false);
  const isRemovingSlip = ref(false);
  const slipDragActive = ref(false);
  const slipUploadError = ref('');
  const slipUploadProgress = ref('');
  const showLocationMapFavoriteEditor = ref(false);
  const savingLocationMapFavorite = ref(false);
  const locationMapFavoriteError = ref('');
  const locationMapFavoriteDraft = reactive({ name: '', favorite: false });

  const slipItems = computed(() => {
    return parseSlipReferences(form.value['Slip Reference']).map((ref) => ({
      ...ref,
      displayName: slipDisplayName(ref),
      previewUrl: ref.thumbnailUrl || getSlipPreviewUrl(ref.fileUrl),
      isPdf: isSlipPdf(ref),
    }));
  });

  // Existing document links — shown even before the user clicks "generate"
  // this session, so a job that already has PDFs surfaces them immediately
  // instead of only after a fresh (re)generation.
  const DOCUMENT_PALETTE: Record<string, { icon: string; classes: string }> = {
    'ใบเสนอราคา': { icon: 'ph-file-pdf', classes: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' },
    'ใบแจ้งหนี้ (Invoice)': {
      icon: 'ph-file-text',
      classes: 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100',
    },
    'ใบเสร็จรับเงิน': {
      icon: 'ph-receipt',
      classes: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100',
    },
  };
  const DOCUMENT_NUMBER_FIELD: Record<string, string> = {
    'ใบเสนอราคา': 'Quotation Number',
    'ใบแจ้งหนี้ (Invoice)': 'Invoice Number',
    'ใบเสร็จรับเงิน': 'Receipt Number',
  };
  const { refreshDocumentAvailability, isDocumentAvailable } = useDocumentAvailability();
  const documentAvailabilitySource = computed(() => jobFormModal.isOpen
    ? getJobDocumentHistory(form.value).map((doc) => doc.pdfUrl).join('\n')
    : '');
  watch(documentAvailabilitySource, (signature) => {
    void refreshDocumentAvailability(signature.split('\n').filter(Boolean));
  }, { immediate: true });
  const documentLinks = computed(() => {
    const history = getJobDocumentHistory(form.value);
    const source = (['quotation', 'invoice', 'receipt'] as DocumentType[]).flatMap((type) => {
      const docs = history.filter((doc) => doc.type === type);
      const prefix = type[0].toUpperCase() + type.slice(1);
      const latestUrl = String(form.value[`${prefix} PDF URL`] || '');
      const doc = docs.find((row) => row.pdfUrl === latestUrl) || docs[docs.length - 1];
      return doc ? [{ ...doc, label: DOCUMENT_LABELS[type] }] : [];
    });
    const client = String(form.value.Client || '').trim();
    return source.filter((doc) => isDocumentAvailable(doc.pdfUrl)).map((doc) => {
      const number =
        doc.number || String(form.value[DOCUMENT_NUMBER_FIELD[doc.label]] || '').trim();
      const filename = [doc.label, number || client].filter(Boolean).join('-') || doc.label;
      const installment = doc.installmentId ? depositInstallments.value.find((row) => row.id === doc.installmentId) : undefined;
      const installmentIndex = installment ? depositInstallments.value.indexOf(installment) : -1;
      return {
        ...doc,
        installmentLabel: installment && installmentIndex >= 0 ? getInstallmentLabel(installment, installmentIndex) : '',
        ...(DOCUMENT_PALETTE[doc.label] || DOCUMENT_PALETTE['ใบเสนอราคา']),
        downloadUrl: getDocumentDownloadUrl(doc.pdfUrl, filename),
      };
    });
  });
  const documentHistoryLinks = computed(() => getJobDocumentHistory(form.value).filter((doc) => isDocumentAvailable(doc.pdfUrl) && !documentLinks.value.some((latest) => documentFileKey(latest.pdfUrl) === documentFileKey(doc.pdfUrl))).slice().reverse().map((doc) => {
    const installment = doc.installmentId ? depositInstallments.value.find((row) => row.id === doc.installmentId) : undefined;
    const installmentIndex = installment ? depositInstallments.value.indexOf(installment) : -1;
    return {
      ...doc, label: DOCUMENT_LABELS[doc.type],
      installmentLabel: installment && installmentIndex >= 0 ? getInstallmentLabel(installment, installmentIndex) : '',
      downloadUrl: getDocumentDownloadUrl(doc.pdfUrl, `${DOCUMENT_LABELS[doc.type]}-${doc.number || form.value.Client}`),
    };
  }));
  const documentsFolderUrl = computed(() => generatedFolderUrl.value || getJobFolderUrl(form.value));

  const hasSlipAttachment = computed(() => slipItems.value.length > 0);
  const canAddMoreSlips = computed(() => slipItems.value.length < SLIP_MAX_FILES);
  function applyPersonalUseToForm() {
    const personal = !!form.value.PersonalUse;
    const notes = String(form.value.Notes || '');
    const hasTag = notes.includes(PERSONAL_JOB_TAG);
    if (personal) {
      form.value.JobType = JOB_TYPE_PERSONAL;
      if (!hasTag) {
        const next = (notes ? notes.trim() + '\n' : '') + PERSONAL_JOB_TAG;
        form.value.Notes = next;
      }
      return;
    }
    // Unchecked: keep JobType if user changed to something else, but remove personal tag.
    if (String(form.value.JobType || '').trim() === JOB_TYPE_PERSONAL) {
      form.value.JobType = '';
    }
    if (hasTag) {
      form.value.Notes = notes
        .split(/\r?\n/)
        .filter((line) => !line.includes(PERSONAL_JOB_TAG))
        .join('\n')
        .trim();
    }
  }

  const isQuotationPreviewOpen = ref(false);
  const quotationPreviewHtml = ref('');

  const eventDateChips = computed(() => {
    const list: string[] = Array.isArray(form.value['_eventDatesList']) ? form.value['_eventDatesList'] : [];
    return [...list].sort().map((value) => ({ value, label: formatEventDateDisplayCompact(value) }));
  });

  const timePicker = reactive({
    open: false,
    part: 'start' as 'start' | 'end',
    hour: '09',
    minute: '00',
  });

  const jobFormReminderSettings = reactive<JobFormReminderSettings>({
    useDefault: true,
    reminders: [],
  });

  let eventDatePicker: any = null;
  let eventMultiDatePicker: any = null;

  const isEdit = computed(() => !!jobFormModal.rowIndex);
  const configData = computed(() => configStore.config || {});
  const locationMapFavorites = computed(() =>
    parseLocationMapFavorites(configData.value.LOCATION_MAP_FAVORITES_JSON)
      .slice()
      .sort((a, b) => Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name, 'th')),
  );
  const currentMapIsFavorite = computed(() => {
    const mapUrl = String(form.value['Google Maps Link'] || '').trim();
    return !!mapUrl && locationMapFavorites.value.some((item) => item.mapUrl === mapUrl && item.favorite);
  });
  const depositFeatureAvailable = computed(() => String(configData.value.QUOTATION_DEPOSIT_ENABLED || 'true').toLowerCase() !== 'false');
  const depositSystemEnabled = computed(() => depositFeatureAvailable.value && String(form.value['Deposit Enabled'] ?? 'true').toLowerCase() !== 'false');
  const finalInstallment = computed(() => depositSystemEnabled.value
    ? depositInstallments.value.slice().reverse().find((row) => row.kind === 'final')
    : undefined);
  const eventDayTimeRows = computed(() => {
    const days: string[] = Array.isArray(form.value['_eventDatesList']) ? form.value['_eventDatesList'] : [];
    return [...days].sort().map((key) => ({ key, label: formatEventDateDisplayCompact(key) }));
  });
  const paymentSummary = computed(() => summarizePayments(quotedTotal.value, depositInstallments.value, paymentLedger.value, formatDateKey(new Date(), 'Asia/Bangkok'), form.value['Apply Tax'] ? taxPercent.value : 0));
  const depositPlannedTotal = computed(() => paymentSummary.value.planned);
  const depositExtraTotal = computed(() => paymentSummary.value.extraPlanned);
  const contractTotalDue = computed(() => paymentSummary.value.totalDue);
  const depositPaidTotal = computed(() => paymentSummary.value.paid);
  const depositBalanceTotal = computed(() => paymentSummary.value.remaining);
  const paymentEntrySuggestedAmount = computed(() => {
    const remaining = paymentSummary.value.remaining;
    if (paymentEntryDraft.installmentId === '$balance') return remaining;
    const round = paymentSummary.value.rounds.find((item) => item.id === paymentEntryDraft.installmentId);
    return money(Math.min(remaining, round?.outstanding ?? remaining));
  });
  const receiptPayments = computed(() => paymentLedger.value.entries.filter((entry) => !entry.voidedAt));
  const paymentHistoryRows = computed(() => paymentLedger.value.entries.slice().reverse().map((entry) => {
    const index = depositInstallments.value.findIndex((row) => row.id === entry.installmentId);
    const receipt = getJobDocumentHistory(form.value).filter((doc) => doc.paymentId === entry.id).at(-1);
    const roundLabel = index >= 0
      ? getSimpleInstallmentLabel(depositInstallments.value[index], index)
      : entry.note === 'ยอดคงเหลือ' ? 'ยอดคงเหลือ' : 'รับเงินไม่ระบุรอบ';
    return { ...entry, roundLabel, hasReceipt: !!receipt, receiptUrl: receipt && isDocumentAvailable(receipt.pdfUrl) ? receipt.pdfUrl : '' };
  }));
  const finalBalancePayments = computed(() => paymentHistoryRows.value.filter((entry) => entry.note === 'ยอดคงเหลือ' && !entry.voidedAt));
  const finalBalanceReceivedTotal = computed(() => finalBalancePayments.value.reduce((total, entry) => total + money(entry.amount), 0));
  const finalBalanceReceivedDate = computed(() => finalBalancePayments.value
    .slice()
    .sort((a, b) => String(b.paidDate || '').localeCompare(String(a.paidDate || ''))
      || String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0]?.paidDate || '');
  const otherUnassignedPaymentTotal = computed(() => Math.max(0,
    paymentSummary.value.unassignedPaid - finalBalanceReceivedTotal.value));
  const canReceiveFinalBalance = computed(() => {
    if (!depositSystemEnabled.value || finalInstallment.value || depositBalanceTotal.value <= 0) return false;
    const plannedDeposits = depositInstallments.value.filter((row) => row.kind !== 'final' && money(row.amount) > 0);
    return plannedDeposits.length > 0 && plannedDeposits.every((row) =>
      (paymentSummary.value.rounds.find((round) => round.id === row.id)?.outstanding ?? 0) <= 0);
  });
  const voidedPaymentHistoryRows = computed(() => paymentHistoryRows.value.filter((entry) => !!entry.voidedAt));
  const quotationPaymentChoices = computed(() => {
    if (!depositSystemEnabled.value || !depositInstallments.value.length) return [];
    return [
      { id: '$full', label: 'ยอดคงเหลือทั้งงาน', amount: depositBalanceTotal.value, dueDate: String(form.value['Payment Due Date'] || ''), paid: depositBalanceTotal.value <= 0 },
      ...depositInstallments.value.map((row, index) => {
        const summary = paymentSummary.value.rounds.find((round) => round.id === row.id);
        return {
          id: row.id,
          label: getSimpleInstallmentLabel(row, index),
          amount: summary?.outstanding ?? money(row.amount),
          dueDate: row.dueDate,
          paid: (summary?.outstanding ?? 0) <= 0,
        };
      }),
    ];
  });
  const clientTemplates = computed(() =>
    parseClientTemplates(configData.value.QUOTATION_CLIENT_TEMPLATES || '[]'),
  );
  const companyDetailTemplates = computed(() =>
    clientTemplates.value
      .map((client, index) => ({
        index,
        label: client.label || client.clientName || `Template ${index + 1}`,
        detail: composeClientCompanyDetail(client),
      }))
      .filter((item) => String(item.detail || '').trim() !== ''),
  );

  function getSimpleInstallmentLabel(row: DepositInstallment, index: number): string {
    if (row.kind === 'final') return 'ยอดคงเหลือ';
    const depositNumber = depositInstallments.value
      .slice(0, index + 1)
      .filter((item) => item.kind !== 'final').length;
    return `มัดจำรอบที่ ${depositNumber}`;
  }

  function getInstallmentReceivedDate(installmentId: string): string {
    const entries = paymentLedger.value.entries
      .filter((entry) => !entry.voidedAt && entry.installmentId === installmentId)
      .slice()
      .sort((a, b) => String(b.paidDate || '').localeCompare(String(a.paidDate || ''))
        || String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    return entries[0]?.paidDate || '';
  }
  const taxPercent = computed(() => {
    const n = parseFloat(configData.value.QUOTATION_TAX_PERCENT || '3');
    return isNaN(n) ? 3 : n;
  });
  const autoPaymentDuePreview = computed(() =>
    computeAutoPaymentDueInput(form.value['Event Date']),
  );
  const quotedTotal = computed(() =>
    calculateQuotedTotal(form.value['Price per Unit'], !!form.value['Apply Tax'], taxPercent.value),
  );
  const quotedGrossTotal = computed(() => {
    const multiplier = 1 - taxPercent.value / 100;
    return form.value['Apply Tax'] && multiplier > 0 ? money(quotedTotal.value / multiplier) : quotedTotal.value;
  });
  const googleCalendarEventColors = GOOGLE_CALENDAR_EVENT_COLORS;
  const selectedCalendarColorLabel = computed(() => {
    const color = getGoogleCalendarEventColor(form.value['Color']);
    if (!color) return 'ใช้สีเริ่มต้นของปฏิทิน';
    return `เลือกแล้ว: ${color.nameTh}`;
  });

  function composeClientCompanyDetail(client: ClientTemplate): string {
    const detail = String(client.detail || '').trim();
    const tel = String(client.tel || '').trim();
    const lines = detail ? detail.split('\n').map((l) => l.trim()).filter(Boolean) : [];
    if (tel && !lines.some((l) => /^tel\s*:/i.test(l))) lines.push(`Tel: ${tel}`);
    return lines.join('\n');
  }

  function openLocationMapFavoriteEditor(fromStar = false) {
    locationMapFavoriteError.value = '';
    const location = String(form.value.Location || '').trim();
    const mapUrl = String(form.value['Google Maps Link'] || '').trim();
    if (fromStar) {
      if (!mapUrl) {
        locationMapFavoriteError.value = 'ใส่ลิงก์ Google Maps ก่อน แล้วจึงกดดาวเพื่อบันทึกเป็นรายการโปรด';
        return;
      }
      const existing = locationMapFavorites.value.find((item) => item.mapUrl === mapUrl);
      if (existing) {
        void persistLocationMapFavorites(locationMapFavorites.value.map((item) =>
          item.id === existing.id ? { ...item, favorite: !item.favorite } : item,
        ));
        return;
      }
    }
    locationMapFavoriteDraft.name = location || String(form.value['Job Name'] || '').trim();
    locationMapFavoriteDraft.favorite = fromStar;
    showLocationMapFavoriteEditor.value = true;
  }

  function cancelLocationMapFavoriteEditor() {
    if (savingLocationMapFavorite.value) return;
    showLocationMapFavoriteEditor.value = false;
    locationMapFavoriteDraft.name = '';
    locationMapFavoriteDraft.favorite = false;
    locationMapFavoriteError.value = '';
  }

  async function persistLocationMapFavorites(items: LocationMapFavorite[]) {
    savingLocationMapFavorite.value = true;
    locationMapFavoriteError.value = '';
    try {
      await saveConfig({ LOCATION_MAP_FAVORITES_JSON: JSON.stringify(items) });
      return true;
    } catch (e: any) {
      locationMapFavoriteError.value = `บันทึกรายการสถานที่ไม่สำเร็จ: ${String(e?.message || e)}`;
      return false;
    } finally {
      savingLocationMapFavorite.value = false;
    }
  }

  async function saveLocationMapFavorite() {
    const name = locationMapFavoriteDraft.name.trim();
    const location = String(form.value.Location || '').trim();
    const mapUrl = String(form.value['Google Maps Link'] || '').trim();
    if (!name) {
      locationMapFavoriteError.value = 'กรอกชื่อสถานที่หรือชื่อแผนที่ก่อนบันทึก';
      return;
    }
    if (!location && !mapUrl) {
      locationMapFavoriteError.value = 'กรอกสถานที่หรือลิงก์แผนที่อย่างน้อยหนึ่งอย่าง';
      return;
    }
    const items = parseLocationMapFavorites(configData.value.LOCATION_MAP_FAVORITES_JSON);
    const sameMapIndex = mapUrl ? items.findIndex((item) => item.mapUrl === mapUrl) : -1;
    const sameNameIndex = items.findIndex((item) => item.name.toLocaleLowerCase() === name.toLocaleLowerCase());
    if (sameNameIndex >= 0 && sameMapIndex !== sameNameIndex) {
      locationMapFavoriteError.value = 'มีชื่อนี้อยู่แล้ว กรุณาใช้ชื่อสถานที่/แผนที่ที่ไม่ซ้ำกัน';
      return;
    }
    const existingIndex = sameMapIndex >= 0 ? sameMapIndex : sameNameIndex;
    const existing = existingIndex >= 0 ? items[existingIndex] : undefined;
    const savedItem: LocationMapFavorite = {
      id: existing?.id || `location-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      name,
      location,
      mapUrl,
      favorite: locationMapFavoriteDraft.favorite || !!existing?.favorite,
    };
    if (existingIndex >= 0) items.splice(existingIndex, 1, savedItem);
    else items.push(savedItem);
    if (await persistLocationMapFavorites(items)) cancelLocationMapFavoriteEditor();
  }

  function applyLocationMapFavorite(item: LocationMapFavorite) {
    form.value.Location = item.location;
    form.value['Google Maps Link'] = item.mapUrl;
  }

  async function toggleLocationMapFavorite(itemId: string) {
    await persistLocationMapFavorites(locationMapFavorites.value.map((item) =>
      item.id === itemId ? { ...item, favorite: !item.favorite } : item,
    ));
  }

  function resetFormState() {
    form.value = getEmptyJobForm();
    cancelLocationMapFavoriteEditor();
    depositInstallments.value = [];
    resetPaymentState();
    showDocumentHistory.value = false;
    quotationPaymentSelectionId.value = '';
    sameTimeForAllDays.value = true;
    Object.keys(eventDayTimes).forEach((key) => delete eventDayTimes[key]);
    paymentDueMode.value = 'auto';
    selectedCompanyTemplateIndex.value = '';
    error.value = '';
    success.value = '';
    generatedDocuments.value = [];
    generatedFolderUrl.value = '';
    saving.value = false;
    generatingDocuments.value = false;
    deleting.value = false;
    syncingCalendar.value = false;
    sendingLine.value = false;
    isUploadingSlip.value = false;
    isUploadingPaymentSlip.value = false;
    paymentSlipFileName.value = '';
    isRemovingSlip.value = false;
    slipUploadError.value = '';
    slipUploadProgress.value = '';
    isQuotationPreviewOpen.value = false;
    quotationPreviewHtml.value = '';
    Object.assign(jobFormReminderSettings, getDefaultCalendarReminderSettings());
    destroyEventDatePicker();
    destroyEventMultiDatePicker();
  }

  function syncDepositScheduleFromForm() {
    depositInstallments.value = parseDepositSchedule(form.value['Deposit Schedule']).map((row, index) => ({ ...row, id: row.id || `legacy-round-${index + 1}` }));
    paymentLedger.value = parsePaymentLedger(form.value['Payment Ledger']) || { version: 1, openingReceived: Math.max(0, money(form.value['Actual Received'])), entries: [] };
    legacyPaymentReviewRequired.value = isEdit.value && !form.value['Payment Ledger'] && depositInstallments.value.length > 0 && !String(form.value['Actual Received'] ?? '').trim();
    legacyReceivedDraft.value = '';
    paymentEntryDraft.open = false;
    receiptPaymentId.value = paymentLedger.value.entries.filter((entry) => !entry.voidedAt).at(-1)?.id || '';
  }

  function resetPaymentState() {
    paymentLedger.value = { version: 1, openingReceived: 0, entries: [] };
    paymentEntryDraft.open = false;
    paymentEntryError.value = '';
    receiptPaymentId.value = '';
    legacyPaymentReviewRequired.value = false;
    legacyReceivedDraft.value = '';
  }

  function confirmLegacyReceived() {
    const raw = String(legacyReceivedDraft.value).trim();
    const amount = Number(raw.replace(/,/g, ''));
    if (!raw || !Number.isFinite(amount) || amount < 0) { error.value = 'ระบุยอดที่ได้รับจริงจากงานเดิม หรือ 0 หากยังไม่รับเงิน'; return; }
    paymentLedger.value.openingReceived = money(amount);
    legacyPaymentReviewRequired.value = false;
    error.value = '';
    success.value = 'ยืนยันยอดรับเงินเดิมแล้ว — กดบันทึกงานเพื่อจัดเก็บ';
  }

  function syncEventDayTimesFromForm() {
    Object.keys(eventDayTimes).forEach((key) => delete eventDayTimes[key]);
    Object.assign(eventDayTimes, parseEventDayTimes(form.value['Event Day Times']));
    sameTimeForAllDays.value = Object.keys(eventDayTimes).length === 0;
  }

  function syncEventDayTimesToSelectedDates() {
    const selected = new Set(Array.isArray(form.value['_eventDatesList']) ? form.value['_eventDatesList'] : []);
    Object.keys(eventDayTimes).forEach((key) => {
      if (!selected.has(key)) delete eventDayTimes[key];
    });
  }

  function applyNewFormDefaults() {
    const sigDefault = configData.value.QUOTATION_INCLUDE_SIGNATURE_DEFAULT;
    form.value['Include Signature'] = sigDefault !== 'false';
    form.value['Show Provider Name on Quotation'] =
      configData.value.QUOTATION_SHOW_PROVIDER_PERSON_NAME_DEFAULT !== 'false';
    // Default event date to today so LINE/calendar always get a date unless
    // the user clears chips (still blocked by required-date validation).
    if (!String(form.value['Event Date'] || '').trim() && !(form.value['_eventDatesList'] || []).length) {
      const today = formatDateForInput(new Date());
      form.value['Event Date'] = today;
      form.value['_eventDatesList'] = [today];
      if (paymentDueMode.value === 'auto') syncPaymentDueModeToForm();
    }
  }

  function syncPaymentDueModeToForm() {
    // A final installment owns the final deadline. Keep only one editable
    // date source, without turning reminders back on when explicitly disabled.
    if (finalInstallment.value && paymentDueMode.value !== 'none') {
      paymentDueMode.value = 'manual';
      form.value['Payment Due Mode'] = 'manual';
      form.value['Payment Due Date'] = finalInstallment.value.dueDate;
      return;
    }
    form.value['Payment Due Mode'] = paymentDueMode.value;
    if (paymentDueMode.value === 'auto') {
      const autoDate = computeAutoPaymentDueInput(form.value['Event Date']);
      if (autoDate) form.value['Payment Due Date'] = autoDate;
    } else if (paymentDueMode.value === 'none') {
      form.value['Payment Due Date'] = '';
    }
  }

  function syncAutoInstallmentDueDates() {
    const autoDate = computeAutoPaymentDueInput(form.value['Event Date']);
    if (!autoDate) return;
    depositInstallments.value.forEach((row) => {
      if (row.dueDateMode === 'auto') row.dueDate = autoDate;
    });
    if (finalInstallment.value?.dueDateMode === 'auto') syncPaymentDueModeToForm();
  }

  function setInstallmentDueDateMode(index: number, mode: 'auto' | 'manual') {
    const row = depositInstallments.value[index];
    if (!row) return;
    row.dueDateMode = mode;
    if (mode === 'auto') {
      row.dueDate = computeAutoPaymentDueInput(form.value['Event Date']) || row.dueDate;
    }
    if (row.kind === 'final') syncPaymentDueModeToForm();
  }

  function setInstallmentDueDate(index: number, date: string) {
    const row = depositInstallments.value[index];
    if (!row) return;
    row.dueDate = date;
    row.dueDateMode = 'manual';
    if (row.kind === 'final') syncPaymentDueModeToForm();
  }

  function syncJobFormRemindersFromField() {
    try {
      const raw = form.value.Reminders;
      if (raw) {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        jobFormReminderSettings.useDefault = parsed.useDefault !== false;
        jobFormReminderSettings.reminders = Array.isArray(parsed.reminders)
          ? parsed.reminders.map((rem: CalendarReminder) => ({
              method: String(rem.method || 'popup').toLowerCase() === 'email' ? 'email' : 'popup',
              value: Math.max(1, parseInt(String(rem.value), 10) || 1),
              unit: ['minutes', 'hours', 'days', 'weeks'].includes(rem.unit) ? rem.unit : 'minutes',
            }))
          : [];
        if (parsed.useDefault === false) jobFormReminderSettings.useDefault = false;
        return;
      }
    } catch {
      /* use default */
    }
    Object.assign(jobFormReminderSettings, getDefaultCalendarReminderSettings());
  }

  function persistJobFormRemindersToField() {
    if (jobFormReminderSettings.useDefault) {
      form.value.Reminders = '';
    } else {
      form.value.Reminders = JSON.stringify({
        useDefault: false,
        reminders: jobFormReminderSettings.reminders.map((rem) => ({
          method: rem.method === 'email' ? 'email' : 'popup',
          value: Math.max(1, parseInt(String(rem.value), 10) || 1),
          unit: ['minutes', 'hours', 'days', 'weeks'].includes(rem.unit) ? rem.unit : 'minutes',
        })),
      });
    }
  }

  function getGlobalCalendarRemindersSummary(): string {
    try {
      const raw = configData.value.CALENDAR_REMINDERS_JSON;
      if (!raw) return '1 วัน, 4 ชม.';
      const parsed = JSON.parse(raw);
      const reminders = parsed?.reminders || getDefaultCalendarReminderSettings().reminders;
      return reminders
        .map((r: CalendarReminder) => `${r.value} ${r.unit === 'days' ? 'วัน' : r.unit === 'hours' ? 'ชม.' : 'นาที'}`)
        .join(', ');
    } catch {
      return '1 วัน, 4 ชม.';
    }
  }

  function destroyEventDatePicker() {
    if (eventDatePicker) {
      eventDatePicker.destroy();
      eventDatePicker = null;
    }
  }

  function destroyEventMultiDatePicker() {
    if (eventMultiDatePicker) {
      eventMultiDatePicker.destroy();
      eventMultiDatePicker = null;
    }
  }

  // All currently selected days, merged from both pickers below — dragging a
  // range and tapping individual days in the multi-select calendar both
  // write here; chip removal reads/writes here too.
  // `skipMultiSync` avoids redrawing the multi-select calendar mid-click when
  // IT was the thing that just triggered this update (it's already correct).
  function applyEventDatesSet(datesSet: Set<string>, skipMultiSync = false) {
    const sorted = Array.from(datesSet).sort();
    form.value['_eventDatesList'] = sorted;
    form.value['Event Date'] = sorted[0] || '';
    syncEventDayTimesToSelectedDates();
    if (paymentDueMode.value === 'auto') syncPaymentDueModeToForm();
    if (!skipMultiSync && eventMultiDatePicker) {
      eventMultiDatePicker.setDate(sorted, false);
    }
  }

  function currentEventDatesSet(): Set<string> {
    return new Set(Array.isArray(form.value['_eventDatesList']) ? form.value['_eventDatesList'] : []);
  }

  // Expands a dragged/clicked range into individual days and UNIONS them into
  // the existing selection (doesn't replace it) — so a dragged span can be
  // combined with separately tapped single days.
  function mergeEventDateRangeIntoForm(startDate: Date, endDate: Date) {
    const existing = currentEventDatesSet();
    let cur = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    let end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    if (cur.getTime() > end.getTime()) {
      const tmp = cur;
      cur = end;
      end = tmp;
    }
    while (cur.getTime() <= end.getTime()) {
      existing.add(formatDateForInput(cur));
      cur.setDate(cur.getDate() + 1);
    }
    applyEventDatesSet(existing);
  }

  function removeEventDate(value: string) {
    const existing = currentEventDatesSet();
    existing.delete(value);
    applyEventDatesSet(existing);
  }

  function initEventDatePicker() {
    const input = document.getElementById('eventDateRangeInput');
    if (!input || typeof flatpickr === 'undefined') return;
    destroyEventDatePicker();
    eventDatePicker = flatpickr(input, {
      // 'range' — drag (or click twice) to add a contiguous span in one go.
      // Each completed drag is UNIONED into the day list (see below) rather
      // than replacing it, so a dragged span can combine with tapped days.
      // The input itself is just a gesture tool — it always resets after
      // each drag; the chips + multi-select calendar are the true state.
      mode: 'range',
      dateFormat: 'Y-m-d',
      defaultDate: [],
      onChange: (selectedDates: Date[]) => {
        if (selectedDates.length !== 2) return;
        mergeEventDateRangeIntoForm(selectedDates[0], selectedDates[1]);
        eventDatePicker.clear(false);
      },
      onClose: () => {
        const pending: Date[] = eventDatePicker.selectedDates || [];
        if (pending.length === 1) {
          mergeEventDateRangeIntoForm(pending[0], pending[0]);
          eventDatePicker.clear(false);
        }
      },
    });
  }

  function initEventMultiDatePicker() {
    const input = document.getElementById('eventDateMultiInput');
    if (!input || typeof flatpickr === 'undefined') return;
    destroyEventMultiDatePicker();
    const defaultDates: string[] = Array.isArray(form.value['_eventDatesList'])
      ? form.value['_eventDatesList']
      : [];
    eventMultiDatePicker = flatpickr(input, {
      // 'multiple' — tap each day directly to toggle it in/out of the
      // selection; the calendar stays open across taps (no extra "add"
      // button, no per-day popup). Starts pre-loaded with every day already
      // selected (incl. ones added via the drag-range input above) so each
      // tap's resulting array is the correct new full state — see
      // applyEventDatesSet, which keeps this calendar in sync when the
      // selection changes from elsewhere (drag-range, chip removal).
      mode: 'multiple',
      dateFormat: 'Y-m-d',
      defaultDate: defaultDates,
      onChange: (selectedDates: Date[]) => {
        const inputs = selectedDates.map((d) => eventMultiDatePicker.formatDate(d, 'Y-m-d'));
        applyEventDatesSet(new Set(inputs), true);
      },
    });
  }

  async function loadExisting() {
    resetFormState();
    if (!configStore.config || !Object.keys(configStore.config).length) {
      await reloadConfig();
    }
    if (jobFormModal.draftId) {
      const draft = loadJobDrafts().find((d) => d.id === jobFormModal.draftId);
      if (draft) {
        form.value = loadJobFormFromRecord(draft.job);
        syncDepositScheduleFromForm();
        syncEventDayTimesFromForm();
        paymentDueMode.value = inferPaymentDueMode(form.value);
        syncJobFormRemindersFromField();
        await nextTick();
        initEventDatePicker();
        initEventMultiDatePicker();
        return;
      }
    }
    if (jobFormModal.prefillJob) {
      form.value = loadJobFormFromRecord(jobFormModal.prefillJob);
      syncDepositScheduleFromForm();
      syncEventDayTimesFromForm();
      paymentDueMode.value = inferPaymentDueMode(form.value);
      syncJobFormRemindersFromField();
      await nextTick();
      initEventDatePicker();
      initEventMultiDatePicker();
      return;
    }
    if (jobFormModal.rowIndex) {
      if (!state.jobs.length) await reload();
      const job = state.jobs.find(
        (j: any) =>
          Number(j._rowIndex) === jobFormModal.rowIndex &&
          (!jobFormModal.sheetName || j._sheetName === jobFormModal.sheetName),
      );
      if (job) {
        form.value = loadJobFormFromRecord(job);
        syncDepositScheduleFromForm();
        syncEventDayTimesFromForm();
        paymentDueMode.value = inferPaymentDueMode(form.value);
        syncJobFormRemindersFromField();
      } else {
        closeJobForm();
        await showAlertModal({
          title: 'ไม่พบงาน',
          message: 'ไม่พบงานที่ต้องการแก้ไข (อาจถูกลบไปแล้ว) — กรุณารีเฟรชแล้วลองใหม่',
          variant: 'warning',
        });
        return;
      }
    } else {
      applyNewFormDefaults();
      syncDepositScheduleFromForm();
      syncEventDayTimesFromForm();
      syncJobFormRemindersFromField();
    }
    await nextTick();
    initEventDatePicker();
    initEventMultiDatePicker();
  }

  function validateSelectedWorkingTimes(): boolean {
    if (sameTimeForAllDays.value) return validateWorkingTime(form.value['Working Time']);
    return eventDayTimeRows.value.every((row) => validateWorkingTime(eventDayTimes[row.key] || ''));
  }

  function normalizeSelectedWorkingTimes() {
    if (sameTimeForAllDays.value) {
      form.value['Working Time'] = normalizeWorkingTimeInput(form.value['Working Time']);
      return;
    }
    eventDayTimeRows.value.forEach((row) => {
      eventDayTimes[row.key] = normalizeWorkingTimeInput(eventDayTimes[row.key] || '');
    });
    // Keep the legacy field useful for old consumers and for single-day jobs.
    const firstTime = eventDayTimeRows.value.map((row) => eventDayTimes[row.key]).find(Boolean);
    if (firstTime) form.value['Working Time'] = firstTime;
  }

  function validateDepositSchedule(): boolean {
    error.value = '';
    if (isUploadingPaymentSlip.value) {
      error.value = 'กำลังอัปโหลดหลักฐานสลิป กรุณารอให้อัปโหลดเสร็จก่อนบันทึกงาน';
      return false;
    }
    if (paymentEntryDraft.open) {
      const hasUnconfirmedPaymentData = money(paymentEntryDraft.amount) > 0
        || !!String(paymentEntryDraft.slipUrl || '').trim()
        || !!String(paymentEntryDraft.reference || '').trim()
        || !!String(paymentEntryDraft.note || '').trim();
      if (!hasUnconfirmedPaymentData) {
        // Opening a receipt form is not itself a payment. Let Save proceed
        // when the draft is empty; the dedicated confirm button records money.
        paymentEntryDraft.open = false;
      } else {
        error.value = 'ช่องรับเงินจริงยังเป็นแบบร่าง — ถ้ามีเงินเข้าจริงให้กดยืนยันรับเงินจริง; ถ้ายังไม่ได้รับเงินจริงให้กดยกเลิกช่องนี้ ไม่ต้องกรอกยอดเพื่อบันทึกงาน';
        return false;
      }
    }
    // Turning off the deposit plan must not turn off actual-payment tracking.
    // The simple (non-deposit) flow still records the received amount and date.
    if (!depositSystemEnabled.value) {
      const received = money(form.value['Actual Received']);
      const receivedDate = String(form.value['Payment Date'] || '').trim();
      if (received > 0 && !receivedDate) {
        error.value = 'กรุณาระบุวันที่ได้รับเงินจริง';
        return false;
      }
      if (receivedDate && received <= 0) {
        error.value = 'กรุณาระบุยอดที่ได้รับเงินจริง';
        return false;
      }
      return true;
    }
    if (legacyPaymentReviewRequired.value) { error.value = 'งานเดิมยังไม่มีข้อมูลแยกระหว่างแผนมัดจำและเงินที่รับจริง กรุณายืนยันยอดรับเงินเดิมก่อนบันทึก'; return false; }
    if (depositPlannedTotal.value > money(quotedTotal.value)) {
      error.value = 'ยอดรวมงวดชำระเกินยอดเต็มทั้งงาน กรุณาตรวจสอบจำนวนเงิน';
      return false;
    }
    for (const [index, row] of depositInstallments.value.entries()) {
      const amount = Number(String(row.amount ?? '').replace(/,/g, ''));
      const roundSummary = paymentSummary.value.rounds.find((r) => r.id === row.id);
      const paid = roundSummary?.received || 0;
      const grossAmount = roundSummary?.grossAmount ?? amount;
      const roundLabel = getSimpleInstallmentLabel(row, index);
      if (!Number.isFinite(amount) || grossAmount < paid || amount < 0) {
        error.value = `${roundLabel}: ยอดงวดรวมส่วนเพิ่มต้องไม่น้อยกว่ายอดรับเงินจริงของงวดนี้`;
        return false;
      }
      if (!String(row.dueDate || '').trim()) {
        error.value = `${roundLabel}: กรุณากำหนดวันครบกำหนดชำระ`;
        return false;
      }
    }
    return true;
  }

  function locateSaveError(message: string) {
    const value = String(message || '');
    if (value.includes('เวลาเริ่ม') || value.includes('เวลาแต่ละวัน')) {
      return { anchor: 'working-time', label: 'เวลางาน — กรอกเวลาเริ่มและเวลาสิ้นสุดให้ครบ', focus: '[data-validation-focus="working-time"]' };
    }
    if (value.includes('วันที่งาน') || value.includes('เลือกวันที่งาน')) {
      return { anchor: 'event-date', label: 'วันที่งาน — เลือกอย่างน้อยหนึ่งวัน', focus: '[data-validation-focus="event-date"]' };
    }
    if (value.includes('ยืนยันยอดรับเงินจริงเดิม') || value.includes('ยอดรับเงินจริงเดิม')) {
      return { anchor: 'legacy-payment-review', label: 'ยอดรับเงินจริงเดิม — ยืนยันยอดที่เคยได้รับก่อนบันทึก', focus: '[data-validation-focus="legacy-payment-review"]' };
    }
    if (value.includes('กำลังอัปโหลดหลักฐานสลิป')) {
      return { anchor: 'payment-entry', label: 'หลักฐานสลิป — รอให้อัปโหลดไฟล์เสร็จก่อนบันทึกงาน', focus: '[data-validation-focus="payment-entry"]' };
    }
    if (value.includes('วันที่ได้รับเงินจริง') || value.includes('ยอดที่ได้รับเงินจริง')) {
      const isDateMissing = value.includes('วันที่ได้รับเงินจริง');
      return {
        anchor: 'legacy-payment',
        label: isDateMissing ? 'ยอดที่ได้รับจริง — ระบุวันที่ได้รับเงิน' : 'ยอดที่ได้รับจริง — ระบุจำนวนเงินจริงที่ได้รับ',
        focus: `[data-validation-focus="${isDateMissing ? 'received-date' : 'actual-received'}"]`,
      };
    }
    if (value.includes('เพิ่มรายการรับเงินหรือยกเลิกช่องรับเงิน') || value.includes('ช่องรับเงินจริงยังเป็นแบบร่าง')) {
      return { anchor: 'payment-entry', label: 'ช่องบันทึกรับเงินจริง — ยืนยันเฉพาะเมื่อได้รับเงินจริงแล้ว หรือยกเลิกช่องนี้', focus: '[data-validation-focus="payment-entry"]' };
    }
    if (value.includes('ใบเสนอราคา') || value.includes('เลือกยอด')) {
      return { anchor: 'quotation-payment-selection', label: 'ยอดในใบเสนอราคา — เลือกยอดเต็มคงเหลือหรือมัดจำ/งวดที่ต้องการ', focus: '[data-validation-focus="quotation-payment-selection"]' };
    }
    if (value.includes('วันครบกำหนด') || value.includes('ยอดรวมงวด') || value.includes('ยอดตามแผน') || value.includes('รหัสงวด') || value.includes('งวดที่อ้างอิง') || value.includes('ยอดรับเงินจริงเกิน')) {
      const isDueDateError = value.includes('วันครบกำหนด');
      const invalidIndex = isDueDateError
        ? depositInstallments.value.findIndex((row) => !String(row.dueDate || '').trim())
        : depositInstallments.value.findIndex((row) => !Number.isFinite(Number(row.amount)) || (paymentSummary.value.rounds.find((round) => round.id === row.id)?.grossAmount ?? Number(row.amount)) < (paymentSummary.value.rounds.find((round) => round.id === row.id)?.received || 0));
      const index = Math.max(0, invalidIndex);
      const round = depositInstallments.value[index];
      const focus = round
        ? `[data-validation-focus="installment-${isDueDateError ? 'due-date' : 'amount'}-${index + 1}"]`
        : '[data-validation-focus="installment-amount-1"]';
      const field = isDueDateError ? 'วันครบกำหนด' : 'ยอดตามแผน/ยอดรับเงินจริง';
      const label = round ? `${getSimpleInstallmentLabel(round, index)} — ตรวจ${field}` : `แผนมัดจำ — ตรวจ${field}`;
      return { anchor: 'deposit-schedule', label, focus };
    }
    if (value.includes('วันที่รับเงินจริง') || value.includes('ข้อมูลการรับเงิน') || value.includes('รายการรับเงิน') || value.includes('ลิงก์หลักฐาน') || value.includes('เหตุผลในการยกเลิก')) {
      return { anchor: 'payment-history', label: 'ประวัติรับเงินจริง — ตรวจวันที่/ยอด/หลักฐานของรายการที่บันทึกไว้', focus: '[data-validation-focus="payment-history"]' };
    }
    return { anchor: 'error-banner', label: 'ข้อความแจ้งปัญหาด้านบนของแบบฟอร์ม', focus: '[data-validation-focus="error-banner"]' };
  }

  async function reportSaveError(message: string, title = 'บันทึกงานไม่สำเร็จ') {
    const detail = String(message || 'ไม่สามารถบันทึกงานได้ กรุณาตรวจสอบข้อมูลแล้วลองอีกครั้ง');
    const destination = locateSaveError(detail);
    const alertMessage = `${detail}\n\nตำแหน่งที่ต้องแก้: ${destination.label}`;
    error.value = alertMessage;
    await showAlertModal({ title, message: alertMessage, variant: 'warning' });
    await nextTick();
    const scrollArea = document.querySelector<HTMLElement>('[data-job-form-scroll]');
    const target = scrollArea?.querySelector<HTMLElement>(`[data-validation-anchor="${destination.anchor}"]`);
    if (!scrollArea || !target) return;
    scrollArea.querySelectorAll('.validation-focus').forEach((element) => element.classList.remove('validation-focus'));
    target.classList.add('validation-focus');
    const scrollRect = scrollArea.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    scrollArea.scrollTo({
      top: scrollArea.scrollTop + targetRect.top - scrollRect.top - (scrollArea.clientHeight - targetRect.height) / 2,
      behavior: 'smooth',
    });
    const focusTarget = target.querySelector<HTMLElement>(destination.focus);
    focusTarget?.focus({ preventScroll: true });
    window.setTimeout(() => target.classList.remove('validation-focus'), 4000);
  }

  async function requireQuotationPaymentChoice(): Promise<boolean> {
    if (!quotationPaymentChoices.value.length) return true;
    const selected = quotationPaymentChoices.value.find((choice) => choice.id === quotationPaymentSelectionId.value);
    if (selected) return true;
    await reportSaveError('กรุณาเลือกก่อนว่าใบเสนอราคานี้จะเรียกเก็บยอดเต็มคงเหลือ หรือยอดของมัดจำ/งวดใด', 'กรุณาเลือกยอดในใบเสนอราคา');
    return false;
  }

  function buildSavePayload() {
    persistJobFormRemindersToField();
    syncPaymentDueModeToForm();
    const payload: JobFormRecord = { ...form.value };
    // PDF generation is explicit via the dedicated button below. Do not
    // persist checkbox selections as auto-generation flags during Save.
    payload['Create Quotation'] = false;
    payload['Create Receipt'] = false;
    payload['Apply Tax'] = !!payload['Apply Tax'];
    payload['Include Signature'] = !!payload['Include Signature'];
    payload['Show Provider Name on Quotation'] = !!payload['Show Provider Name on Quotation'];
    payload['Show Payment Due on Quotation'] = !!payload['Show Payment Due on Quotation'];
    payload['Event Date'] = buildEventDateValue(form.value);
    // Keep a saved schedule when the global feature is turned off so it can be
    // restored later; rendering and the form simply hide it while disabled.
    if (depositSystemEnabled.value) {
      payload['Deposit Schedule'] = serializeDepositSchedule(depositInstallments.value);
      payload['Payment Ledger'] = JSON.stringify(paymentLedger.value);
      payload['Actual Received'] = String(depositPaidTotal.value);
    }
    payload['Event Day Times'] = sameTimeForAllDays.value ? '' : serializeEventDayTimes(eventDayTimes);
    payload['Quotation Date'] = formatDateToSheet(payload['Quotation Date']);
    payload['Price per Unit'] = String(payload['Price per Unit'] ?? '').trim();
    payload.Status = normalizeJobStatus(payload.Status);
    payload['Color'] = normalizeGoogleCalendarColorId(payload['Color']);
    payload['Payment Due Mode'] = paymentDueMode.value;
    if (paymentDueMode.value === 'auto') {
      const autoDate = computeAutoPaymentDueInput(form.value['Event Date']);
      if (autoDate) payload['Payment Due Date'] = autoDate;
    } else if (paymentDueMode.value === 'none') {
      payload['Payment Due Date'] = '';
    }
    // Edit identity comes from the modal opener — never trust a blank form
    // _rowIndex after a previous "new job" session left the form open.
    if (jobFormModal.rowIndex) {
      payload._rowIndex = jobFormModal.rowIndex;
      if (jobFormModal.sheetName) payload._sheetName = jobFormModal.sheetName;
    }
    delete payload['_eventDatesList'];
    return payload;
  }

  // Shared by the main Save/Update button and the dedicated "สร้างเอกสาร PDF"
  // button below. The main Save/Update action only saves the job; document
  // generation is enabled only when the dedicated action passes force flags.
  async function performSave(
    opts: { forceCreateQuotation?: boolean; forceCreateInvoice?: boolean; forceCreateReceipt?: boolean; receiptDraft?: boolean; requireNoDuplicateDocuments?: boolean; replaceQuotation?: boolean; replaceInvoice?: boolean; replaceReceipt?: boolean; receiptPaymentId?: string; quotationPaymentInstallmentId?: string; documentInstallmentId?: string; skipOverwrite?: boolean; keepOpen?: boolean } = {},
  ) {
    if (!validateSelectedWorkingTimes()) {
      await reportSaveError(sameTimeForAllDays.value
        ? 'กรุณากรอกเวลาเริ่มและเวลาสิ้นสุด เช่น 09:00 - 17:00'
        : 'กรุณากรอกเวลาแต่ละวันให้ครบรูปแบบ เช่น 09:00 - 17:00');
      return;
    }
    const eventDateValue = buildEventDateValue(form.value);
    if (!String(eventDateValue || '').trim()) {
      await reportSaveError('กรุณาเลือกวันที่งานก่อนบันทึก');
      return;
    }
    if (!validateDepositSchedule()) {
      await reportSaveError(error.value || 'ตรวจสอบยอดงวดชำระ: ต้องมีวันครบกำหนด และยอดต้องไม่ต่ำกว่ายอดรับเงินจริงของงวดนั้น');
      return;
    }
    const paymentError = validatePaymentData(buildSavePayload(), formatDateKey(new Date(), 'Asia/Bangkok'), form.value['Apply Tax'] ? taxPercent.value : 0);
    if (paymentError) { await reportSaveError(paymentError); return; }
    normalizeSelectedWorkingTimes();
    applyPersonalUseToForm();
    saving.value = true;
    generatingDocuments.value = !!(opts.forceCreateQuotation || opts.forceCreateInvoice || opts.forceCreateReceipt);
    error.value = '';
    success.value = '';
    generatedDocuments.value = [];
    generatedFolderUrl.value = '';
    try {
      const payload = buildSavePayload();
      payload['Event Date'] = eventDateValue;
      const createQuotation = !!opts.forceCreateQuotation;
      const createReceipt = !!opts.forceCreateReceipt;
      const saveOptions = {
        createQuotation,
        createReceipt,
        requireNoDuplicateDocuments: opts.requireNoDuplicateDocuments,
        replaceQuotation: opts.replaceQuotation,
        replaceInvoice: opts.replaceInvoice,
        replaceReceipt: opts.replaceReceipt,
        receiptDraft: opts.receiptDraft,
        createInvoice: !!opts.forceCreateInvoice,
        receiptPaymentId: opts.receiptPaymentId,
        quotationPaymentInstallmentId: opts.quotationPaymentInstallmentId,
        documentInstallmentId: opts.documentInstallmentId,
        skipOverwrite: opts.skipOverwrite,
      };
      let result: Awaited<ReturnType<typeof save>>;
      try {
        result = await save(payload, saveOptions);
      } catch (saveError: any) {
        if (!(saveError instanceof ApiRequestError) || saveError.code !== 'schedule_conflict') {
          throw saveError;
        }
        const confirmed = await showConfirmModal({
          title: 'พบคิวงานซ้อน',
          message: String(saveError.message || 'พบงานในวันหรือเวลาที่ทับกัน ต้องการบันทึกคิวนี้ต่อหรือไม่?'),
          confirmText: 'บันทึกงานนี้ต่อ',
          cancelText: 'กลับไปแก้วัน/เวลา',
          variant: 'warning',
        });
        if (!confirmed) {
          error.value = 'ยังไม่ได้บันทึกงาน — ข้อมูลยังอยู่ในแบบฟอร์ม สามารถแก้วัน/เวลาหรือกดบันทึกเพื่อยืนยันคิวซ้อนได้';
          return;
        }
        result = await save(payload, { ...saveOptions, allowScheduleConflict: true });
      }
      // Treat document selections as one-shot actions. Clearing them after a
      // PDF run prevents a later receipt-only run in the same open form from
      // accidentally regenerating and replacing the quotation/invoice too.
      if (createQuotation || createReceipt) {
        form.value['Create Quotation'] = false;
        form.value['Create Receipt'] = false;
      }
      // Keep identity on the open form so a second save (e.g. สร้าง PDF)
      // updates the same row instead of appending a duplicate job.
      if (result?.job) {
        Object.assign(form.value, result.job);
        // Sheet dates use DD/MM/YYYY while HTML date inputs require ISO keys.
        form.value['Quotation Date'] = loadJobFormFromRecord(result.job)['Quotation Date'];
        form.value['Payment Date'] = loadJobFormFromRecord(result.job)['Payment Date'];
        form.value['Payment Due Date'] = loadJobFormFromRecord(result.job)['Payment Due Date'];
        syncDepositScheduleFromForm();
        form.value.JobID = result.job.JobID || form.value.JobID;
        form.value._rowIndex = result.rowIndex;
        form.value._sheetName = result.sheetName;
        jobFormModal.rowIndex = result.rowIndex;
        jobFormModal.sheetName = result.sheetName;
      }
      let msg = result.isNew ? 'สร้างงานเรียบร้อย' : 'บันทึกเรียบร้อย';
      const cal = result.calendarSync;
      if (cal?.status === 'synced') {
        msg += ' · เชื่อม Google Calendar แล้ว';
      } else if (cal?.status === 'failed') {
        msg += ` · Calendar ไม่สำเร็จ: ${cal.message}`;
      } else if (cal?.status === 'skipped') {
        msg += ` · Calendar: ${cal.message}`;
      }

      const docs: { label: string; number?: string; pdfUrl: string }[] = [];
      const docFailures: string[] = [];
      if (result.quotation?.status === 'synced' && result.quotation.pdfUrl) {
        docs.push({ label: `ใบเสนอราคา${opts.documentInstallmentId ? ` — ${getDocumentInstallmentLabel(opts.documentInstallmentId)}` : ''}`, number: result.quotation.number, pdfUrl: result.quotation.pdfUrl });
      } else if (result.quotation?.status === 'failed') {
        docFailures.push(`ใบเสนอราคา: ${result.quotation.message}`);
      }
      if (result.invoice?.status === 'synced' && result.invoice.pdfUrl) {
        // Label must match the DOCUMENT_PALETTE / DOCUMENT_NUMBER_FIELD keys
        // (and the existing-job path) so the freshly generated row gets the
        // right icon/number instead of falling back to the quotation palette.
        docs.push({ label: `ใบแจ้งหนี้ (Invoice)${opts.documentInstallmentId ? ` — ${getDocumentInstallmentLabel(opts.documentInstallmentId)}` : ''}`, number: result.invoice.number, pdfUrl: result.invoice.pdfUrl });
      } else if (result.invoice?.status === 'failed') {
        docFailures.push(`ใบแจ้งหนี้: ${result.invoice.message}`);
      }
      if (result.receipt?.status === 'synced' && result.receipt.pdfUrl) {
        docs.push({ label: `${opts.receiptDraft ? 'ใบเสร็จรับเงิน (ฉบับร่าง)' : 'ใบเสร็จรับเงิน'}${opts.documentInstallmentId ? ` — ${getDocumentInstallmentLabel(opts.documentInstallmentId)}` : ''}`, number: result.receipt.number, pdfUrl: result.receipt.pdfUrl });
      } else if (result.receipt?.status === 'failed') {
        docFailures.push(`ใบเสร็จรับเงิน: ${result.receipt.message}`);
      }
      generatedDocuments.value = docs;
      generatedFolderUrl.value = docs.length ? getJobFolderUrl(result.job) : '';
      if (docFailures.length) msg += ` · สร้างเอกสารไม่สำเร็จ: ${docFailures.join(', ')}`;

      success.value = msg;
      if (docFailures.length) {
        error.value = docFailures.join(' · ');
        await showAlertModal({ title: 'งานบันทึกแล้ว แต่สร้างเอกสารไม่สำเร็จ', message: docFailures.join('\n'), variant: 'warning' });
      }
      if (jobFormModal.draftId) removeDraft(jobFormModal.draftId);
      if (opts.keepOpen) {
        // Stay open after PDF generate so the new file links remain visible.
      } else if (isEdit.value) {
        closeJobForm();
      } else if (!docs.length) {
        setTimeout(() => closeJobForm(), 800);
      }
    } catch (e: any) {
      await reportSaveError(String(e?.message || e));
    } finally {
      saving.value = false;
      generatingDocuments.value = false;
    }
  }

  async function submit() {
    await performSave();
  }

  // Dedicated action button — saves the job and forces PDF generation for
  // whichever document types are checked. The main Save button never renders
  // documents, even when these selections are enabled.
  function getDocumentInstallmentLabel(installmentId: string): string {
    const index = depositInstallments.value.findIndex((row) => row.id === installmentId);
    return index >= 0 ? getSimpleInstallmentLabel(depositInstallments.value[index], index) : 'มัดจำ';
  }

  function getDocumentsForInstallment(installmentId: string) {
    return getJobDocumentHistory(form.value).filter((doc) => doc.installmentId === installmentId && isDocumentAvailable(doc.pdfUrl)).map((doc) => ({
      ...doc, label: DOCUMENT_LABELS[doc.type],
      downloadUrl: getDocumentDownloadUrl(doc.pdfUrl, `${DOCUMENT_LABELS[doc.type]}-${doc.number || form.value.Client}`),
    }));
  }

  async function generateDocumentsNow(type: DocumentType | 'quotation-invoice' = 'quotation', selectedPaymentId?: string, documentInstallmentId?: string) {
    if (saving.value) return;
    const wantQuotation = type === 'quotation' || type === 'quotation-invoice';
    const wantInvoice = type === 'invoice' || type === 'quotation-invoice';
    const wantReceipt = type === 'receipt';
    if (wantQuotation && !documentInstallmentId && !(await requireQuotationPaymentChoice())) return;
    const paymentId = selectedPaymentId || (wantReceipt && documentInstallmentId ? undefined : receiptPaymentId.value);
    const history = getJobDocumentHistory(form.value);
    const hasExistingReceipt = wantReceipt
      ? paymentId
        ? history.some((doc) => doc.paymentId === paymentId)
        : history.some((doc) => doc.type === 'receipt'
          && !doc.paymentId
          && (documentInstallmentId ? doc.installmentId === documentInstallmentId : !doc.installmentId))
      : false;
    const quotationScope = documentInstallmentId
      || (quotationPaymentSelectionId.value && quotationPaymentSelectionId.value !== '$full' ? quotationPaymentSelectionId.value : undefined);
    const hasExistingQuotation = wantQuotation && hasDocumentVersionForScope(history, 'quotation', quotationScope);
    const hasExistingInvoice = wantInvoice && hasDocumentVersionForScope(history, 'invoice', documentInstallmentId);
    let replaceQuotation = false;
    let replaceInvoice = false;
    if (hasExistingQuotation || hasExistingInvoice) {
      const existingTypes = [
        hasExistingQuotation ? 'ใบเสนอราคา' : '',
        hasExistingInvoice ? 'Invoice' : '',
      ].filter(Boolean).join(' และ ');
      const confirmed = await showConfirmModal({
        title: `มียอดนี้ใน${existingTypes}แล้ว`,
        message: 'ต้องการสร้าง PDF ฉบับใหม่และเปลี่ยนลิงก์หลักไปฉบับใหม่หรือไม่? ไฟล์เดิมจะยังเก็บไว้ในประวัติเอกสาร ไม่ถูกลบ',
        confirmText: 'สร้างฉบับใหม่แทนลิงก์หลัก',
        cancelText: 'ไม่ทับ — ยกเลิก',
        variant: 'warning',
      });
      if (!confirmed) return;
      replaceQuotation = hasExistingQuotation;
      replaceInvoice = hasExistingInvoice;
    }
    let replaceReceipt = false;
    if (hasExistingReceipt) {
      const confirmed = await showConfirmModal({
        title: 'ใบเสร็จของรายการนี้มีอยู่แล้ว',
        message: 'สร้างใบเสร็จฉบับใหม่และแทนลิงก์ปัจจุบันหรือไม่? ไฟล์ฉบับเดิมจะไม่ถูกลบ และยังเปิดได้จากประวัติเอกสาร',
        confirmText: 'สร้างใหม่และแทนที่ลิงก์',
        cancelText: 'ไม่ทับ — ยกเลิก',
        variant: 'warning',
      });
      if (!confirmed) return;
      replaceReceipt = true;
    }
    await performSave({
      forceCreateQuotation: wantQuotation,
      forceCreateInvoice: wantInvoice,
      forceCreateReceipt: wantReceipt,
      requireNoDuplicateDocuments: true,
      replaceQuotation,
      replaceInvoice,
      replaceReceipt,
      receiptDraft: wantReceipt && !paymentId && (receiptPayments.value.length > 0 || depositPaidTotal.value <= 0),
      receiptPaymentId: wantReceipt ? paymentId : undefined,
      quotationPaymentInstallmentId: wantQuotation && !documentInstallmentId ? quotationPaymentSelectionId.value : undefined,
      documentInstallmentId,
      skipOverwrite: true,
      keepOpen: true,
    });
  }

  async function syncCalendarNow() {
    if (syncingCalendar.value) return;
    if (!validateSelectedWorkingTimes()) {
      error.value = sameTimeForAllDays.value
        ? 'กรุณากรอกเวลาเริ่มและเวลาสิ้นสุด เช่น 09:00 - 17:00'
        : 'กรุณากรอกเวลาแต่ละวันให้ครบรูปแบบ เช่น 09:00 - 17:00';
      return;
    }
    normalizeSelectedWorkingTimes();
    applyPersonalUseToForm();
    syncingCalendar.value = true;
    error.value = '';
    success.value = '';
    try {
      const payload = buildSavePayload();
      const result = await api.syncJobToCalendar(payload);
      const cal = result.calendarSync;
      if (cal?.status === 'synced') {
        success.value = 'เชื่อม Google Calendar แล้ว';
      } else if (cal?.status === 'skipped') {
        error.value = `ข้าม Calendar: ${cal.message}`;
      } else {
        error.value = `เชื่อม Calendar ไม่สำเร็จ: ${cal?.message || ''}`;
      }
    } catch (e: any) {
      error.value = 'เชื่อม Calendar ไม่สำเร็จ: ' + String(e?.message || e);
    } finally {
      syncingCalendar.value = false;
    }
  }

  // Resends the job summary card to LINE (admin/team) without saving —
  // ports v1's manual "LINE" button (sendJobLineNotification_). Only
  // meaningful for an already-saved job, so only shown/enabled when editing.
  async function sendLineNow() {
    if (sendingLine.value || !isEdit.value) return;
    sendingLine.value = true;
    error.value = '';
    success.value = '';
    try {
      const payload = buildSavePayload();
      const result = await api.notifyJobLine(payload);
      if (result.success) {
        success.value = result.message || 'ส่งข้อความ LINE แล้ว';
      } else {
        error.value = 'ส่งข้อความ LINE ไม่สำเร็จ: ' + (result.message || '');
      }
    } catch (e: any) {
      error.value = 'ส่งข้อความ LINE ไม่สำเร็จ: ' + String(e?.message || e);
    } finally {
      sendingLine.value = false;
    }
  }

  function saveDraft() {
    const draftJob: JobFormRecord = {
      ...form.value,
      'Event Date': buildEventDateValue(form.value),
      'Event Day Times': sameTimeForAllDays.value ? '' : serializeEventDayTimes(eventDayTimes),
    };
    if (depositSystemEnabled.value) {
      draftJob['Deposit Schedule'] = serializeDepositSchedule(depositInstallments.value);
      draftJob['Payment Ledger'] = JSON.stringify(paymentLedger.value);
      draftJob['Actual Received'] = String(depositPaidTotal.value);
    }
    // If we're already editing a draft, update it in place instead of creating
    // a duplicate. For a brand-new draft, remember its id so pressing Save again
    // keeps updating the same one rather than piling up copies.
    if (jobFormModal.draftId) {
      const updated = updateDraft(jobFormModal.draftId, draftJob);
      if (updated) {
        success.value = 'อัปเดตร่างแล้ว';
        return;
      }
    }
    const draft = addDraft(draftJob);
    jobFormModal.draftId = draft.id;
    success.value = 'บันทึกร่างแล้ว';
  }

  async function deleteCurrentJob() {
    if (!isEdit.value || deleting.value) return;
    const label = form.value.Client || form.value.JobID || '';
    const ok = await showConfirmModal({
      title: 'ลบงาน',
      message: `ลบงาน "${label}" ?`,
      confirmText: 'ลบ',
      variant: 'danger',
    });
    if (!ok) return;
    deleting.value = true;
    error.value = '';
    try {
      await remove(form.value);
      closeJobForm();
    } catch (e: any) {
      error.value = 'ลบไม่สำเร็จ: ' + String(e?.message || e);
    } finally {
      deleting.value = false;
    }
  }

  function resetForm() {
    form.value = getEmptyJobForm();
    depositInstallments.value = [];
    resetPaymentState();
    sameTimeForAllDays.value = true;
    Object.keys(eventDayTimes).forEach((key) => delete eventDayTimes[key]);
    paymentDueMode.value = 'auto';
    applyNewFormDefaults();
    syncJobFormRemindersFromField();
    selectedCompanyTemplateIndex.value = '';
    destroyEventDatePicker();
    destroyEventMultiDatePicker();
    nextTick(() => {
      initEventDatePicker();
      initEventMultiDatePicker();
    });
  }

  function handleClose() {
    // Document generation (Quotation/Invoice/Receipt PDFs) can take 20-40+
    // seconds. If we let the modal close mid-save, the backend keeps working
    // and DOES finish — but the user never sees the success links since the
    // modal (and its generatedDocuments state) is already hidden.
    if (saving.value) return;
    closeJobForm();
  }

  function applyCompanyDetailTemplate() {
    const idx = parseInt(selectedCompanyTemplateIndex.value, 10);
    const item = companyDetailTemplates.value.find((t) => t.index === idx);
    if (item) form.value['Quotation Company Detail'] = item.detail;
  }

  function clearQuotationCompanyDetail() {
    form.value['Quotation Company Detail'] = '';
    selectedCompanyTemplateIndex.value = '';
  }

  function pickClientTemplate(client: ClientTemplate) {
    useClientTemplate(form.value, client);
  }

  async function openFormQuotationPreview() {
    if (!(await requireQuotationPaymentChoice())) return;
    const layout = parseQuotationLayoutFromConfig(configData.value);
    const previewJob = buildSavePayload();
    quotationPreviewHtml.value = buildQuotationLayoutPreviewHtml(previewJob, layout || { blocks: getDefaultQuotationLayoutBlocks() }, configData.value, {
      signatureImageSrc: getSignatureImageUrl(),
      quotationPaymentInstallmentId: quotationPaymentSelectionId.value || undefined,
    });
    isQuotationPreviewOpen.value = true;
  }

  function closeFormQuotationPreview() {
    isQuotationPreviewOpen.value = false;
    quotationPreviewHtml.value = '';
  }

  async function uploadSlipFiles(files: File[]) {
    const accepted = files.filter(isAcceptedSlipFile);
    if (!accepted.length) {
      slipUploadError.value = `รองรับเฉพาะรูปภาพหรือ PDF (${SLIP_SUPPORTED_FORMATS_LABEL})`;
      return;
    }

    const existing = parseSlipReferences(form.value['Slip Reference']);
    const remaining = SLIP_MAX_FILES - existing.length;
    if (remaining <= 0) {
      slipUploadError.value = `อัปโหลดได้สูงสุด ${SLIP_MAX_FILES} ไฟล์`;
      return;
    }

    const toUpload = accepted.slice(0, remaining);
    if (accepted.length > remaining) {
      slipUploadError.value = `เลือก ${accepted.length} ไฟล์ แต่เหลือที่ว่าง ${remaining} ช่อง — อัปโหลด ${toUpload.length} ไฟล์แรก`;
    } else {
      slipUploadError.value = '';
    }

    isUploadingSlip.value = true;
    error.value = '';
    success.value = '';
    const updated: SlipReference[] = [...existing];
    let uploadedCount = 0;
    let sharingWarning = '';

    try {
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        slipUploadProgress.value = `กำลังอัปโหลด ${i + 1}/${toUpload.length}...`;
        const prepared = await prepareSlipFileForUpload(file);
        const res = await api.uploadSlip({
          fileName: prepared.fileName,
          mimeType: prepared.mimeType,
          base64: prepared.base64,
          eventDate: String(form.value['Event Date'] || ''),
          jobTitle: String(form.value['Item / Job Title'] || form.value.Client || ''),
          client: String(form.value.Client || ''),
          jobType: String(form.value.JobType || ''),
          folderUrl: String(form.value['Folder URL'] || ''),
        });
        if (res.sharingWarning) sharingWarning = res.sharingWarning;
        updated.push({
          fileUrl: res.fileUrl,
          fileName: res.fileName || prepared.fileName,
          mimeType: prepared.mimeType,
          thumbnailUrl: res.thumbnailUrl,
        });
        uploadedCount++;
      }
      form.value['Slip Reference'] = serializeSlipReferences(updated);
      success.value =
        sharingWarning ||
        (uploadedCount === 1 ? 'อัปโหลดสลิปเรียบร้อย' : `อัปโหลดสลิป ${uploadedCount} ไฟล์เรียบร้อย`);
    } catch (e: any) {
      if (updated.length > existing.length) {
        form.value['Slip Reference'] = serializeSlipReferences(updated);
      }
      const message = String(e?.message || e || 'อัปโหลดสลิปไม่สำเร็จ');
      error.value = message;
      if (!slipUploadError.value) slipUploadError.value = message;
    } finally {
      isUploadingSlip.value = false;
      slipUploadProgress.value = '';
    }
  }

  async function handlePaymentSlipUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!isAcceptedSlipFile(file)) {
      paymentEntryError.value = `รองรับเฉพาะรูปภาพหรือ PDF (${SLIP_SUPPORTED_FORMATS_LABEL})`;
      input.value = '';
      return;
    }

    isUploadingPaymentSlip.value = true;
    paymentEntryError.value = '';
    try {
      const prepared = await prepareSlipFileForUpload(file);
      const result = await api.uploadSlip({
        fileName: prepared.fileName,
        mimeType: prepared.mimeType,
        base64: prepared.base64,
        eventDate: String(form.value['Event Date'] || ''),
        jobTitle: String(form.value['Item / Job Title'] || form.value.Client || ''),
        client: String(form.value.Client || ''),
        jobType: String(form.value.JobType || ''),
        folderUrl: String(form.value['Folder URL'] || ''),
      });
      paymentEntryDraft.slipUrl = result.fileUrl;
      paymentSlipFileName.value = result.fileName || prepared.fileName;
      success.value = result.sharingWarning || 'อัปโหลดหลักฐานสลิปเรียบร้อย';
    } catch (e: any) {
      paymentEntryError.value = String(e?.message || e || 'อัปโหลดหลักฐานสลิปไม่สำเร็จ');
    } finally {
      isUploadingPaymentSlip.value = false;
      input.value = '';
    }
  }

  async function handleSlipUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length) await uploadSlipFiles(files);
    input.value = '';
  }

  async function removeSlipAt(index: number) {
    if (isUploadingSlip.value || isRemovingSlip.value) return;
    const items = parseSlipReferences(form.value['Slip Reference']);
    const target = items[index];
    if (!target) return;
    const ok = await showConfirmModal({
      title: 'ลบไฟล์สลิป',
      message: `ลบไฟล์ "${slipDisplayName(target)}" ?\nไฟล์ใน Google Drive จะถูกลบและไม่สามารถกู้คืนได้`,
      confirmText: 'ลบ',
      variant: 'danger',
    });
    if (!ok) return;

    isRemovingSlip.value = true;
    slipUploadError.value = '';
    error.value = '';
    try {
      await api.deleteSlip({ slipUrl: target.fileUrl });
      items.splice(index, 1);
      form.value['Slip Reference'] = serializeSlipReferences(items);
      success.value = 'ลบสลิปเรียบร้อย';
    } catch (e: any) {
      const message = String(e?.message || e || 'ลบสลิปไม่สำเร็จ');
      error.value = message;
      slipUploadError.value = message;
    } finally {
      isRemovingSlip.value = false;
    }
  }

  watch(
    () => form.value.PersonalUse,
    () => applyPersonalUseToForm(),
  );

  function onSlipDragOver(event: DragEvent) {
    event.preventDefault();
    slipDragActive.value = true;
  }

  function onSlipDragLeave(event: DragEvent) {
    event.preventDefault();
    slipDragActive.value = false;
  }

  async function onSlipDrop(event: DragEvent) {
    event.preventDefault();
    slipDragActive.value = false;
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) await uploadSlipFiles(files);
  }

  function openSlipFilePicker() {
    const input = document.getElementById('job-slip-file-input') as HTMLInputElement | null;
    input?.click();
  }

  function goToCalendarSettings() {
    closeJobForm();
    router.push('/settings');
  }

  // Time picker
  function openTimePicker(part: 'start' | 'end') {
    const normalized = getWorkingTimePart(form.value['Working Time'], part) || (part === 'start' ? '09:00' : '17:00');
    const pieces = normalized.split(':');
    timePicker.open = true;
    timePicker.part = part;
    timePicker.hour = pieces[0] || '09';
    timePicker.minute = pieces[1] || '00';
  }

  function closeTimePicker() {
    timePicker.open = false;
  }

  function handleWorkingTimeBlur() {
    form.value['Working Time'] = normalizeWorkingTimeInput(form.value['Working Time']);
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (!active?.closest?.('.time-picker-root')) closeTimePicker();
    }, 80);
  }

  function syncTimePickerFromInput(part: 'start' | 'end') {
    if (!timePicker.open || timePicker.part !== part) return;
    const normalized = getWorkingTimePart(form.value['Working Time'], part);
    if (!normalized) return;
    const pieces = normalized.split(':');
    timePicker.hour = pieces[0];
    timePicker.minute = pieces[1];
  }

  function buildTimePickerValues(centerValue: string, min: number, max: number): string[] {
    const maxSpan = max - min + 1;
    const center = isNaN(Number(centerValue)) ? min : Number(centerValue);
    const values: string[] = [];
    for (let offset = -2; offset <= 2; offset++) {
      let value = center + offset;
      while (value < min) value += maxSpan;
      while (value > max) value -= maxSpan;
      values.push(String(value).padStart(2, '0'));
    }
    return values;
  }

  function timePickerHourValues() {
    return buildTimePickerValues(timePicker.hour, 0, 23);
  }

  function timePickerMinuteValues() {
    return buildTimePickerValues(timePicker.minute, 0, 59);
  }

  function nudgeTimePicker(unit: 'hour' | 'minute', delta: number) {
    const key = unit === 'hour' ? 'hour' : 'minute';
    const max = unit === 'hour' ? 23 : 59;
    const span = max + 1;
    let value = Number(timePicker[key]);
    if (isNaN(value)) value = 0;
    value = (value + delta + span) % span;
    timePicker[key] = String(value).padStart(2, '0');
  }

  function handleTimePickerWheel(event: WheelEvent, unit: 'hour' | 'minute') {
    nudgeTimePicker(unit, event.deltaY < 0 ? -1 : 1);
  }

  function selectTimePickerValue(unit: 'hour' | 'minute', value: string) {
    const key = unit === 'hour' ? 'hour' : 'minute';
    timePicker[key] = value;
  }

  function applyTimePicker() {
    const value = `${timePicker.hour}:${timePicker.minute}`;
    form.value['Working Time'] = setWorkingTimePart(form.value['Working Time'], timePicker.part, value);
    form.value['Working Time'] = normalizeWorkingTimeInput(form.value['Working Time']);
    closeTimePicker();
  }

  function applyWorkingTimePreset(preset: (typeof WORKING_TIME_PRESETS)[number]) {
    form.value['Working Time'] = `${preset.start} - ${preset.end}`;
    closeTimePicker();
  }

  function onEventDayTimeInput(dateKey: string, value: string) {
    eventDayTimes[dateKey] = value;
  }

  function onTimeModeChange() {
    closeTimePicker();
    if (!sameTimeForAllDays.value) {
      // Starting separate-day mode with the common time pre-fills every day;
      // the user can then adjust only the days that differ.
      const common = normalizeWorkingTimeInput(form.value['Working Time'] || '');
      eventDayTimeRows.value.forEach((row) => {
        if (!eventDayTimes[row.key] && common) eventDayTimes[row.key] = common;
      });
      return;
    }
    // Switching back to one time for all days uses the first per-day value as
    // the common value, so the hidden field never appears blank unexpectedly.
    const first = eventDayTimeRows.value.map((row) => eventDayTimes[row.key]).find(Boolean);
    if (first) form.value['Working Time'] = normalizeWorkingTimeInput(first);
  }

  function setAllDayWorkingTime() {
    closeTimePicker();
    const fullDay = '00:00 - 23:59';
    if (!sameTimeForAllDays.value && eventDayTimeRows.value.length) {
      eventDayTimeRows.value.forEach((row) => {
        eventDayTimes[row.key] = fullDay;
      });
      return;
    }
    form.value['Working Time'] = fullDay;
  }

  function normalizeEventDayTime(dateKey: string) {
    eventDayTimes[dateKey] = normalizeWorkingTimeInput(eventDayTimes[dateKey] || '');
  }

  function addDepositInstallment() {
    const selectedDates: string[] = Array.isArray(form.value['_eventDatesList']) ? form.value['_eventDatesList'] : [];
    depositInstallments.value.push({ id: crypto.randomUUID(), kind: 'deposit', amount: 0, dueDate: selectedDates[0] || autoPaymentDuePreview.value || formatDateForInput(new Date()), dueDateMode: 'manual' });
  }

  function removeDepositInstallment(index: number) {
    if (paymentLedger.value.entries.some((entry) => entry.installmentId === depositInstallments.value[index]?.id)) {
      error.value = 'งวดนี้มีประวัติรับเงินแล้ว จึงลบไม่ได้'; return;
    }
    depositInstallments.value.splice(index, 1);
  }

  function openPaymentEntry(installmentId?: string) {
    const nextRoundId = paymentSummary.value.rounds.find((round) => round.amount > 0 && round.outstanding > 0)?.id || '';
    paymentEntryError.value = '';
    paymentSlipFileName.value = '';
    Object.assign(paymentEntryDraft, { open: true, installmentId: installmentId ?? nextRoundId, amount: '', paidDate: formatDateKey(new Date(), 'Asia/Bangkok'), method: 'โอนเงิน', reference: '', note: '', slipUrl: '' });
  }

  function recordPaymentEntry() {
    if (isUploadingPaymentSlip.value) { paymentEntryError.value = 'รอให้อัปโหลดหลักฐานสลิปเสร็จก่อนเพิ่มรายการรับเงินจริง'; return; }
    const amount = money(paymentEntryDraft.amount);
    if (legacyPaymentReviewRequired.value) { paymentEntryError.value = 'กรุณายืนยันยอดรับเงินเดิมก่อนเพิ่มรายการใหม่'; return; }
    const isBalancePayment = paymentEntryDraft.installmentId === '$balance';
    const round = isBalancePayment ? undefined : paymentSummary.value.rounds.find((r) => r.id === paymentEntryDraft.installmentId);
    if (amount <= 0) { paymentEntryError.value = 'กรอกยอดรับเงินจริงมากกว่า 0 บาท'; return; }
    const maxReceivable = Math.min(depositBalanceTotal.value, round?.outstanding ?? depositBalanceTotal.value);
    if (amount > maxReceivable) {
      const scope = round && round.outstanding < depositBalanceTotal.value
        ? `ยอดค้างของ${getSimpleInstallmentLabel(round, depositInstallments.value.findIndex((item) => item.id === round.id))} ${formatCurrency(round.outstanding)} บาท`
        : `ยอดคงเหลือทั้งงาน ${formatCurrency(depositBalanceTotal.value)} บาท`;
      paymentEntryError.value = `ยอดที่กรอก ${formatCurrency(amount)} บาท เกิน${scope}อยู่ ${formatCurrency(amount - maxReceivable)} บาท กรุณาตรวจยอดเรียกเก็บรวมและยอดคงเหลือที่แสดง`;
      return;
    }
    if (paymentEntryDraft.installmentId && !round && !isBalancePayment) { paymentEntryError.value = 'ไม่พบมัดจำรอบที่เลือก'; return; }
    if (!paymentEntryDraft.paidDate || paymentEntryDraft.paidDate > formatDateKey(new Date(), 'Asia/Bangkok')) { paymentEntryError.value = 'ระบุวันที่รับเงินจริงซึ่งไม่เป็นวันในอนาคต'; return; }
    if (paymentEntryDraft.slipUrl && !/^https:\/\//i.test(paymentEntryDraft.slipUrl)) { paymentEntryError.value = 'ลิงก์หลักฐานต้องเริ่มด้วย https://'; return; }
    const entry: PaymentEntry = { id: crypto.randomUUID(), installmentId: isBalancePayment ? '' : paymentEntryDraft.installmentId, amount, paidDate: paymentEntryDraft.paidDate, method: paymentEntryDraft.method, reference: paymentEntryDraft.reference, note: isBalancePayment ? 'ยอดคงเหลือ' : paymentEntryDraft.note, slipUrl: paymentEntryDraft.slipUrl, createdAt: new Date().toISOString() };
    paymentLedger.value.entries.push(entry);
    receiptPaymentId.value = entry.id;
    form.value['Payment Date'] = entry.paidDate;
    paymentEntryDraft.open = false;
    paymentSlipFileName.value = '';
    success.value = 'เพิ่มรายการรับเงินแล้ว — กดบันทึกงานเพื่อจัดเก็บ';
  }

  async function voidPaymentEntry(id: string) {
    const row = paymentLedger.value.entries.find((r) => r.id === id);
    if (!row || row.voidedAt) return;
    if (getJobDocumentHistory(form.value).some((doc) => doc.paymentId === id)) { error.value = 'รายการนี้ออกใบเสร็จแล้ว ต้องจัดการเอกสารก่อนยกเลิกยอด'; return; }
    const ok = await showConfirmModal({ title: 'ยกเลิกรายการรับเงิน', message: 'ใช้สำหรับรายการที่บันทึกผิด ประวัติเดิมจะยังอยู่และยอดคงเหลือจะคำนวณใหม่', confirmText: 'ยกเลิกรายการ', variant: 'warning' });
    if (!ok) return;
    row.voidedAt = new Date().toISOString();
    row.voidReason = 'ผู้ใช้ยกเลิกรายการที่บันทึกผิด';
    success.value = 'ยกเลิกรายการแล้ว — กดบันทึกงานเพื่อจัดเก็บ';
  }

  function onWorkingTimeInput(part: 'start' | 'end', value: string) {
    form.value['Working Time'] = setWorkingTimePart(form.value['Working Time'], part, value);
    syncTimePickerFromInput(part);
  }

  function onJobFormReminderUseDefaultChange() {
    if (!jobFormReminderSettings.useDefault && !jobFormReminderSettings.reminders.length) {
      jobFormReminderSettings.reminders = JSON.parse(
        JSON.stringify(getDefaultCalendarReminderSettings().reminders),
      );
    }
    persistJobFormRemindersToField();
  }

  function addJobFormReminder() {
    jobFormReminderSettings.reminders.push({ method: 'popup', value: 30, unit: 'minutes' });
    jobFormReminderSettings.useDefault = false;
    persistJobFormRemindersToField();
  }

  function removeJobFormReminder(index: number) {
    jobFormReminderSettings.reminders.splice(index, 1);
    persistJobFormRemindersToField();
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      // A confirm dialog stacked above this modal owns the Escape key —
      // without this, one press would cancel the dialog AND close the form.
      if (confirmModal.show) return;
      if (isQuotationPreviewOpen.value) {
        closeFormQuotationPreview();
        e.preventDefault();
      } else if (jobFormModal.isOpen) {
        e.preventDefault();
        handleClose();
      }
    }
  }

  function handleDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('.time-picker-root')) closeTimePicker();
  }

  watch(
    () => jobFormModal.isOpen,
    (open) => {
      if (open) loadExisting();
      else resetFormState();
    },
  );

  watch(paymentDueMode, () => syncPaymentDueModeToForm());
  watch(() => finalInstallment.value?.dueDate, () => syncPaymentDueModeToForm());
  watch(() => form.value['Event Date'], () => syncAutoInstallmentDueDates());

  onMounted(() => {
    window.addEventListener('keydown', onKeydown);
    document.addEventListener('click', handleDocumentClick);
    if (jobFormModal.isOpen) loadExisting();
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', onKeydown);
    document.removeEventListener('click', handleDocumentClick);
    destroyEventDatePicker();
    destroyEventMultiDatePicker();
  });

  return {
    jobFormModal,
    form,
    paymentDueMode,
    saving,
    generatingDocuments,
    deleting,
    syncingCalendar,
    sendingLine,
    error,
    success,
    generatedDocuments,
    generatedFolderUrl,
    documentLinks,
    documentHistoryLinks,
    showDocumentHistory,
    receiptPaymentId,
    quotationPaymentSelectionId,
    quotationPaymentChoices,
    receiptPayments,
    legacyPaymentReviewRequired,
    legacyReceivedDraft,
    confirmLegacyReceived,
    documentsFolderUrl,
    isEdit,
    configData,
    locationMapFavorites,
    currentMapIsFavorite,
    showLocationMapFavoriteEditor,
    savingLocationMapFavorite,
    locationMapFavoriteDraft,
    locationMapFavoriteError,
    openLocationMapFavoriteEditor,
    cancelLocationMapFavoriteEditor,
    saveLocationMapFavorite,
    applyLocationMapFavorite,
    toggleLocationMapFavorite,
    clientTemplates,
    companyDetailTemplates,
    selectedCompanyTemplateIndex,
    taxPercent,
    autoPaymentDuePreview,
    quotedTotal,
    quotedGrossTotal,
    eventDateChips,
    removeEventDate,
    workingTimePresets: WORKING_TIME_PRESETS,
    timePicker,
    jobFormReminderSettings,
    isUploadingSlip,
    isRemovingSlip,
    slipUploadError,
    slipDragActive,
    hasSlipAttachment,
    canAddMoreSlips,
    slipItems,
    slipUploadProgress,
    SLIP_FILE_ACCEPT,
    SLIP_MAX_FILE_SIZE_LABEL,
    SLIP_SUPPORTED_FORMATS_LABEL,
    isQuotationPreviewOpen,
    quotationPreviewHtml,
    getWorkingTimeInputPart,
    formatPaymentDate,
    formatCurrency,
    submit,
    saveDraft,
    deleteCurrentJob,
    syncCalendarNow,
    sendLineNow,
    generateDocumentsNow,
    resetForm,
    handleClose,
    pickClientTemplate,
    applyCompanyDetailTemplate,
    clearQuotationCompanyDetail,
    openFormQuotationPreview,
    closeFormQuotationPreview,
    handleSlipUpload,
    openSlipFilePicker,
    onSlipDragOver,
    onSlipDragLeave,
    onSlipDrop,
    removeSlipAt,
    goToCalendarSettings,
    getGlobalCalendarRemindersSummary,
    openTimePicker,
    closeTimePicker,
    handleWorkingTimeBlur,
    onWorkingTimeInput,
    timePickerHourValues,
    timePickerMinuteValues,
    nudgeTimePicker,
    handleTimePickerWheel,
    selectTimePickerValue,
    applyTimePicker,
    applyWorkingTimePreset,
    onJobFormReminderUseDefaultChange,
    addJobFormReminder,
    removeJobFormReminder,
    syncPaymentDueModeToForm,
    finalInstallment,
    googleCalendarEventColors,
    selectedCalendarColorLabel,
    depositSystemEnabled,
    depositFeatureAvailable,
    paymentLedger,
    paymentSummary,
    paymentEntryDraft,
    paymentEntryError,
    isUploadingPaymentSlip,
    paymentSlipFileName,
    paymentHistoryRows,
    voidedPaymentHistoryRows,
    finalBalancePayments,
    finalBalanceReceivedTotal,
    finalBalanceReceivedDate,
    otherUnassignedPaymentTotal,
    canReceiveFinalBalance,
    getSimpleInstallmentLabel,
    getInstallmentReceivedDate,
    getInstallmentLabel,
    openPaymentEntry,
    recordPaymentEntry,
    handlePaymentSlipUpload,
    voidPaymentEntry,
    depositInstallments,
    setInstallmentDueDateMode,
    setInstallmentDueDate,
    depositPlannedTotal,
    depositPaidTotal,
    depositBalanceTotal,
    paymentEntrySuggestedAmount,
    depositExtraTotal,
    contractTotalDue,
    addDepositInstallment,
    removeDepositInstallment,
    getDocumentsForInstallment,
    sameTimeForAllDays,
    eventDayTimes,
    eventDayTimeRows,
    onEventDayTimeInput,
    onTimeModeChange,
    setAllDayWorkingTime,
    normalizeEventDayTime,
  };
}
